import { Copy, Mail, MessageCircle, Send, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { usePlayer } from "../context/PlayerContext";
import { useT } from "../lib/i18n";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";

export const ShareDialog = () => {
  const { shareMix, setShareMix } = usePlayer();
  const { t } = useT();
  const url = shareMix ? `${window.location.origin}/mix/${shareMix.id}` : "";
  const text = shareMix ? t("share.text").replace("{t}", shareMix.title) : "";
  const enc = encodeURIComponent;
  const count = () => shareMix && api.post(`/mixes/${shareMix.id}/share`).catch(() => {});

  const targets = [
    ["WhatsApp", MessageCircle, `https://wa.me/?text=${enc(`${text} ${url}`)}`],
    ["X", Share2, `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(url)}`],
    ["Facebook", Share2, `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`],
    ["Telegram", Send, `https://t.me/share/url?url=${enc(url)}&text=${enc(text)}`],
    ["Email", Mail, `mailto:?subject=${enc(text)}&body=${enc(url)}`],
  ];

  const copy = async () => {
    await navigator.clipboard?.writeText(url).catch(() => {});
    count();
    toast.success(t("share.copied"));
  };

  const native = async () => {
    try {
      await navigator.share({ title: text, url });
      count();
    } catch (e) { /* cancelled */ }
  };

  return (
    <Dialog open={!!shareMix} onOpenChange={(o) => !o && setShareMix(null)}>
      <DialogContent data-testid="share-dialog" className="bg-[#07101E] border-white/10 rounded-none max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-5xl font-normal leading-none">{t("share.title")}</DialogTitle>
          <DialogDescription className="text-[#A8B3C4] truncate">{shareMix?.title}</DialogDescription>
        </DialogHeader>
        <div className="flex border border-white/15">
          <input data-testid="share-link-input" readOnly value={url} className="flex-1 bg-transparent px-3 py-3 font-mono text-xs text-[#A8B3C4] min-w-0" />
          <button data-testid="share-copy-button" onClick={copy} className="px-4 bg-[#1FD1FF] text-black flex items-center gap-2 font-mono text-xs uppercase"><Copy className="w-4 h-4" />{t("share.copy")}</button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {targets.map(([name, Icon, href]) => (
            <a key={name} data-testid={`share-${name.toLowerCase()}`} href={href} target="_blank" rel="noopener noreferrer" onClick={count}
              className="flex items-center gap-2 px-3 py-3 border border-white/10 font-mono text-xs uppercase tracking-widest hover:border-[#1FD1FF] hover:text-[#1FD1FF] transition-colors">
              <Icon className="w-4 h-4" />{name}
            </a>
          ))}
          {typeof navigator !== "undefined" && navigator.share && (
            <button data-testid="share-native" onClick={native} className="flex items-center gap-2 px-3 py-3 border border-white/10 font-mono text-xs uppercase tracking-widest hover:border-[#1FD1FF] hover:text-[#1FD1FF] transition-colors">
              <Share2 className="w-4 h-4" />{t("share.more")}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
